// Neon shift palette. Bright, saturated hues that read as
// distinct headline colors at a glance on a white background.
// tint() still pastel-derives header bars from these for the
// schedule grid, so a saturated swatch here picks a punchy
// pastel for the soft surfaces automatically.
//
// Twelve choices is enough for most agencies — each shift /
// special unit gets a distinct hue without forcing supervisors
// to eyeball ten near-identical blues. A "Custom…" input is
// still available in the picker for the rare case an agency
// wants to match their patch color exactly.
export const NEON_SHIFT_COLORS: { hex: string; name: string }[] = [
  { hex: "#ff003c", name: "Neon red" },
  { hex: "#ff5e00", name: "Neon orange" },
  { hex: "#ffcc00", name: "Neon yellow" },
  { hex: "#62d72a", name: "Neon lime" },
  { hex: "#00cc55", name: "Neon emerald" },
  { hex: "#00d9b0", name: "Neon teal" },
  { hex: "#00b3ff", name: "Neon cyan" },
  { hex: "#1e6bff", name: "Neon blue" },
  { hex: "#6e00ff", name: "Neon indigo" },
  { hex: "#b800ff", name: "Neon purple" },
  { hex: "#ff00aa", name: "Neon magenta" },
  { hex: "#ff1493", name: "Neon pink" },
];

// Pick a readable foreground color (white or near-black) for a
// given background hex. Uses the relative-luminance formula
// from WCAG: convert sRGB to linear, weight by 0.2126/0.7152/
// 0.0722, threshold at ~0.5 to flip the text from black to
// white as the background gets darker.
//
// Used by the schedule's shift-section header so a punchy neon
// background still carries legible text — bright yellow keeps
// black text, neon blue / magenta switch to white, no manual
// per-shift override needed.
export function readableTextOn(hex: string): "#0f172a" | "#ffffff" {
  const m = hex.replace("#", "");
  if (m.length !== 6) return "#0f172a";
  const channel = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return "#0f172a";
  const L = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  // 0.5 threshold lands neon yellow / lime / cyan on black text
  // and pushes everything else (reds, blues, purples, magentas)
  // onto white — matches the visual breakpoint a designer would
  // pick by eye.
  return L > 0.5 ? "#0f172a" : "#ffffff";
}

// Mix a hex color with white. `amount` is how much white to blend
// in: 0 returns the original color, 1 returns pure white. Used to
// derive a legible pastel background from a shift's saturated
// marker color (header bars on the Schedule, group headers on the
// Training / Time-off lists, etc.) without adding a second column
// to the shifts table just for the pale version.
//
// Non-hex input (empty string, named colors, rgba strings) is
// returned unchanged so callers don't have to guard against it.
export function tint(hex: string, amount: number): string {
  const m = hex.replace("#", "");
  if (m.length !== 6) return hex;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return hex;
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  const toHex = (c: number) => c.toString(16).padStart(2, "0");
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}
